import { useEffect, useId, useMemo, useRef } from "react";
import DOMPurify from "dompurify";
import { ChevronDown } from "lucide-react";

// Sanitization config: rich text limited to inline emphasis and links.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["strong", "em", "b", "i", "a"],
  ALLOWED_ATTR: ["href", "target", "rel"],
};

// Add target / rel hardening to every <a> the user-supplied HTML may contain.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

// JSON-LD FAQPage payload derived from cards. The schema spec requires the
// answer as a plain string; we strip HTML for that purpose only (the visible
// answer keeps its sanitized HTML).
function buildFaqPageJsonLd(cards) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: cards.map((c) => ({
      "@type": "Question",
      name: c.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: (c.reponse || "").replace(/<[^>]*>/g, "").trim(),
      },
    })),
  };
}

export default function FlashcardFAQ({
  titre = "Questions fréquentes",
  cards,
  withSchema = true,
}) {
  const componentId = useId().replace(/:/g, "");
  const containerRef = useRef(null);

  // Sanitize once per cards change.
  const sanitizedCards = useMemo(
    () =>
      (cards || []).map((c, i) => ({
        ...c,
        _answerId: `ffaq-${componentId}-answer-${i}`,
        _summaryId: `ffaq-${componentId}-summary-${i}`,
        _sanitizedHtml: DOMPurify.sanitize(c.reponse || "", SANITIZE_CONFIG),
      })),
    [cards, componentId]
  );

  // Sync aria-expanded on every <summary> with its parent <details>[open] state.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const detailsList = Array.from(root.querySelectorAll("details.ffaq-item"));
    const cleanups = detailsList.map((d) => {
      const sum = d.querySelector("summary");
      if (!sum) return () => {};
      const sync = () => sum.setAttribute("aria-expanded", d.open ? "true" : "false");
      sync();
      d.addEventListener("toggle", sync);
      return () => d.removeEventListener("toggle", sync);
    });
    return () => cleanups.forEach((fn) => fn());
  }, [sanitizedCards]);

  // Inject / remove FAQPage JSON-LD tied to this component instance.
  useEffect(() => {
    if (!withSchema || sanitizedCards.length === 0) return undefined;
    const id = `ffaq-schema-${componentId}`;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-flashcard-faq-schema", componentId);
    el.id = id;
    el.textContent = JSON.stringify(buildFaqPageJsonLd(sanitizedCards));
    document.head.appendChild(el);
    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, [withSchema, sanitizedCards, componentId]);

  if (sanitizedCards.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="flashcard-faq"
      data-testid={`flashcard-faq-${componentId}`}
    >
      {titre && (
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">{titre}</h2>
      )}

      <ul className="space-y-3 list-none">
        {sanitizedCards.map((c, i) => (
          <li key={c._summaryId}>
            <details className="ffaq-item bg-white border border-slate-200 rounded-2xl">
              <summary
                id={c._summaryId}
                aria-controls={c._answerId}
                className="ffaq-summary flex items-center gap-3 p-5 cursor-pointer select-none rounded-2xl"
                data-testid={`ffaq-summary-${i}`}
              >
                <span className="font-display text-base sm:text-lg font-semibold text-slate-900 flex-1">
                  {c.question}
                </span>
                <ChevronDown
                  className="ffaq-chevron w-5 h-5 text-slate-400 flex-shrink-0 transition-transform"
                  aria-hidden="true"
                />
              </summary>

              {/* Answer wrapper: always mounted; visibility animated via CSS grid-rows */}
              <div
                id={c._answerId}
                role="region"
                aria-labelledby={c._summaryId}
                className="ffaq-answer-wrap"
              >
                <div className="ffaq-answer-inner">
                  <div className="px-5 pb-5 pt-1 text-slate-700 leading-relaxed">
                    <div
                      className="ffaq-answer-html"
                      data-testid={`ffaq-answer-${i}`}
                      dangerouslySetInnerHTML={{ __html: c._sanitizedHtml }}
                    />
                    {c.source && c.source.url && c.source.label && (
                      <p className="text-sm text-slate-500 mt-3">
                        Source :{" "}
                        <a
                          href={c.source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0056D2] hover:underline break-words"
                        >
                          {c.source.label}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
}
